import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  effect,
  inject,
  input,
  signal,
  ChangeDetectionStrategy
} from "@angular/core";

export type AnchorNavigationItem = {
  id: string;
  label: string;
  itemsCount?: number | undefined;
};

@Component({
  selector: "dbw-anchor-navigation",
  templateUrl: "./anchor-navigation.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./anchor-navigation.component.scss",
})
export class AnchorNavigationComponent implements AfterViewInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ngZone = inject(NgZone);

  readonly items = input.required<AnchorNavigationItem[]>();
  readonly ariaLabel = input("Navigation des sections");
  readonly activeItemId = signal<string | undefined>(undefined);

  private rafPending = false;
  private lastScrollTime = 0;
  private scrollContainer?: HTMLElement;
  private readonly scrollListener = () => this.onScroll();

  constructor() {
    effect(() => {
      this.items();
      requestAnimationFrame(() => this.updateActiveItem());
    });
  }

  ngAfterViewInit(): void {
    this.scrollContainer = this.findScrollContainer();

    this.ngZone.runOutsideAngular(() => {
      this.scrollContainer?.addEventListener("scroll", this.scrollListener, {
        passive: true,
      });
      requestAnimationFrame(() => this.updateActiveItem());
    });
  }

  ngOnDestroy(): void {
    this.scrollContainer?.removeEventListener("scroll", this.scrollListener);
  }

  scrollToItem(itemId: string): void {
    document.getElementById(itemId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    this.activeItemId.set(itemId);
  }

  isItemActive(itemId: string, isFirst: boolean): boolean {
    const activeItemId = this.activeItemId();

    return activeItemId ? activeItemId === itemId : isFirst;
  }

  private onScroll(): void {
    const now = performance.now();
    const debounceDelay = 30;
    if (now - this.lastScrollTime < debounceDelay) {
      return;
    }

    this.lastScrollTime = now;

    if (!this.rafPending) {
      this.rafPending = true;
      requestAnimationFrame(() => {
        this.updateActiveItem();
        this.rafPending = false;
      });
    }
  }

  private updateActiveItem(): void {
    const container = this.scrollContainer;
    if (!container) {
      return;
    }

    const items = this.items();
    if (items.length === 0) {
      this.setActiveItem(undefined);
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const containerMid = containerRect.top + containerRect.height / 2;
    let activeItemId: string | undefined;

    for (let index = 0; index < items.length; index++) {
      const currentElement = document.getElementById(items[index].id);
      if (!currentElement || this.isHidden(currentElement)) {
        continue;
      }

      const currentRect = currentElement.getBoundingClientRect();
      const nextElement =
        index + 1 < items.length
          ? document.getElementById(items[index + 1].id)
          : null;
      const nextRect = nextElement ? nextElement.getBoundingClientRect() : null;

      if (nextRect && nextRect.top <= containerMid) {
        activeItemId = items[index + 1].id;
      } else if (currentRect.bottom >= containerMid) {
        activeItemId = items[index].id;
        break;
      }
    }

    this.setActiveItem(activeItemId);
  }

  private findScrollContainer(): HTMLElement {
    let element = this.elementRef.nativeElement.parentElement;

    while (element) {
      const overflowY = window.getComputedStyle(element).overflowY;
      const canScroll = overflowY === "auto" || overflowY === "scroll";

      if (canScroll) {
        return element;
      }

      element = element.parentElement;
    }

    return document.scrollingElement instanceof HTMLElement
      ? document.scrollingElement
      : document.documentElement;
  }

  private isHidden(element: HTMLElement): boolean {
    return element.offsetParent === null;
  }

  private setActiveItem(itemId: string | undefined): void {
    if (this.activeItemId() === itemId) {
      return;
    }

    this.ngZone.run(() => this.activeItemId.set(itemId));
  }
}
